import { Link } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { Kicker } from "@/components/ui/kicker";
import { FindingRow } from "@/components/experience/finding-row";
import { compareFindingsByCriticality } from "@/components/experience/vocabulary";
import {
  resolveFindingTitle,
  resolveFindingDescription,
  resolveFindingType,
  resolveFindingSeverity,
} from "@/lib/finding-resolution";

interface LatestFindingsProps {
  projectId: string;
  walkthroughId: string;
}

export function LatestFindings({
  projectId,
  walkthroughId,
}: LatestFindingsProps) {
  const { data: findings, isLoading } = trpc.finding.list.useQuery({
    projectId,
    walkthroughId,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!findings || findings.length === 0) return null;

  const sorted = [...findings].sort((a, b) =>
    compareFindingsByCriticality(
      { type: resolveFindingType(a), severity: resolveFindingSeverity(a) },
      { type: resolveFindingType(b), severity: resolveFindingSeverity(b) },
    ),
  );
  const top = sorted.slice(0, 3);

  return (
    <div>
      <Kicker>Top findings</Kicker>
      <ol className="mt-2 divide-y divide-border/60 border-y border-border/60">
        {top.map((finding) => (
          <li key={finding.id} className="py-2">
            <FindingRow
              variant="compact"
              href={`/projects/${projectId}/findings/${finding.id}`}
              finding={{
                id: finding.id,
                title: resolveFindingTitle(finding),
                description: resolveFindingDescription(finding) ?? "",
                type: resolveFindingType(finding),
                severity: resolveFindingSeverity(finding),
                stepNumber: (finding as any).stepNumber ?? null,
                walkthroughId: (finding as any).walkthroughId ?? walkthroughId,
                targetBox: (finding as any).targetBox ?? null,
              }}
            />
          </li>
        ))}
      </ol>
      {findings.length > 3 && (
        <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-2.5">
          <Link
            to={`/projects/${projectId}/walkthroughs/${walkthroughId}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline"
          >
            <span>+{findings.length - 3} more findings in full report</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      )}
    </div>
  );
}
